/**
 * Album state — URL-synced via `?album=<id>`.
 *
 * No router; the album swap *is* the navigation. We:
 *   - read the initial id from `?album=` (or the default if missing/invalid),
 *   - keep the URL and React state in lockstep when `setCurrent` is called,
 *   - listen to `popstate` so back/forward buttons rebind correctly.
 *
 * Coming-soon albums are visible in the picker but cannot become current.
 */

import { useCallback, useEffect, useState } from 'react';
import { ALBUMS, AVAILABLE_ALBUMS, DEFAULT_ALBUM_ID } from '@/data/albums';
import type { AlbumModule, AvailableAlbumModule } from '@/types/album';

const SEARCH_PARAM = 'album';
const INIT_KEY = '__NYX_INIT__';

interface NyxInit {
  album?: string;
}

/**
 * Read the initial album id, in priority order:
 *   1. A `globalThis.__NYX_INIT__.album` hint (used by prerender on the server,
 *      and by an inline `<script>` embedded by prerender on the client so
 *      hydration matches the SSR'd subtree exactly).
 *   2. The `?album=<id>` URL search param (only on the client).
 *   3. The default album.
 */
function readInitialAlbum(): string {
  const g = globalThis as { [INIT_KEY]?: NyxInit };
  const hinted = g[INIT_KEY]?.album;
  if (hinted && AVAILABLE_ALBUMS.has(hinted)) return hinted;
  if (typeof window === 'undefined') return DEFAULT_ALBUM_ID;
  const params = new URLSearchParams(window.location.search);
  const raw = params.get(SEARCH_PARAM);
  if (raw && AVAILABLE_ALBUMS.has(raw)) return raw;
  return DEFAULT_ALBUM_ID;
}

function writeUrlAlbum(id: string, replace: boolean): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (id === DEFAULT_ALBUM_ID) {
    url.searchParams.delete(SEARCH_PARAM);
  } else {
    url.searchParams.set(SEARCH_PARAM, id);
  }
  const fn = replace ? 'replaceState' : 'pushState';
  window.history[fn]({}, '', url.toString());
}

export interface CurrentAlbumState {
  /** Currently displayed album id. */
  id: string;
  /** Validated module for the current album (always available). */
  album: AvailableAlbumModule;
  /** All registered modules in display order — for the picker. */
  all: ReadonlyMap<string, AlbumModule>;
  /** Switch to a new available album. No-op if the id is unknown or coming-soon. */
  setCurrent: (id: string) => void;
}

/**
 * Hook: returns the active album + a setter that updates the URL.
 * `pushState` so back-button works; `replaceState` for the initial sync.
 */
export function useCurrentAlbum(): CurrentAlbumState {
  const [id, setId] = useState<string>(() => readInitialAlbum());

  // On mount, ensure the URL matches our default if no param is present.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get(SEARCH_PARAM);
    if (fromUrl && !AVAILABLE_ALBUMS.has(fromUrl)) {
      // Unknown/coming-soon id in URL — fall back to default and clean URL.
      writeUrlAlbum(DEFAULT_ALBUM_ID, true);
    }
  }, []);

  // Back/forward navigation rebinds.
  useEffect(() => {
    const onPop = () => setId(readInitialAlbum());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const setCurrent = useCallback((next: string) => {
    if (!AVAILABLE_ALBUMS.has(next)) return;
    setId((prev) => {
      if (prev === next) return prev;
      writeUrlAlbum(next, false);
      return next;
    });
  }, []);

  const album = AVAILABLE_ALBUMS.get(id);
  if (!album) {
    throw new Error(`useCurrentAlbum: invariant — id "${id}" is not an available album`);
  }

  return { id, album, all: ALBUMS, setCurrent };
}
